import React, { useState } from "react";
import Icon from "components/Icon";
import Section from "components/Section";
import { useT } from "hooks/useT";
import classNames from "classnames";

export interface FaqItem {
  q: string;
  a: string;
}

interface FAQProps {
  faqs: FaqItem[];
  label?: string;
  title?: string;
  subtitle?: string;
  darkmode: boolean;
}

export default function FAQ({
  faqs,
  label,
  title,
  subtitle,
  darkmode,
}: FAQProps) {
  const { t } = useT();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const resolvedLabel = label ?? t("faq_label");
  const resolvedTitle = title ?? t("faq_title");
  const resolvedSubtitle = subtitle ?? t("faq_subtitle");

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <Section
      bg={darkmode ? "zinc-900" : "gray"}
      padding="lg"
      containerClassName="max-w-3xl"
      label={resolvedLabel}
      title={resolvedTitle}
      titleClassName={classNames(
        "text-3xl font-bold tracking-tight sm:text-4xl",
        darkmode ? "text-white" : "text-gray-900"
      )}
      description={resolvedSubtitle}
      className="transition-colors duration-500"
    >
      <dl className="space-y-2">
        {faqs.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className={classNames(
                "rounded-xl border transition-colors duration-200",
                darkmode
                  ? isOpen
                    ? "border-orange-500/40 bg-zinc-700/60"
                    : "border-zinc-700 bg-zinc-700/40 hover:border-zinc-600"
                  : isOpen
                  ? "border-orange-200 bg-white"
                  : "border-gray-200 bg-white hover:border-gray-300"
              )}
            >
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
              >
                <span
                  className={classNames(
                    "text-base font-semibold",
                    darkmode ? "text-white" : "text-gray-900"
                  )}
                >
                  {item.q}
                </span>
                <span
                  className={`shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                >
                  <Icon
                    name="angle down"
                    size="small"
                    color={isOpen ? "orange" : "grey"}
                  />
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p
                  className={classNames(
                    "px-6 pb-5 text-sm leading-relaxed",
                    darkmode ? "text-zinc-400" : "text-gray-600"
                  )}
                >
                  {item.a}
                </p>
              </div>
            </div>
          );
        })}
      </dl>
    </Section>
  );
}
